<!--
  Fornitori ordini dettagli — redesigned 2026-06-09 (v2.5.0).

  Replaces ~536 LOC of kendo-grid + multiple kendo-dialog modals.
  The TS was already a tight state machine on `panelView` ('lots' /
  'add-step1' / 'add-step2' / 'add-step3' / 'view-lot') with Swal2
  confirms and FileReader uploads — no TS changes needed.

  Layout:
    1. Main page card — order header, info cards, DDT card, products
       table, bottom actions (DDT upload, Conferma ordine).
    2. Two modals OUTSIDE `.fod-page`:
         - Product lots modal (selectedProduct)
         - Batch wizard modal (panelView != 'lots') — multi-view:
           view-lot, add-step1, add-step2, add-step3.

  Modals are siblings of `.fod-page` so they can't be trapped by the
  containing block created by parent Ionic `.ion-page` or by
  `.fod-page`'s own scrolling container. Same insulation pattern we
  used for fornitore-list-accounts.
-->

<!--
  Page overlay — this whole component is rendered as a modal overlay
  on top of `<app-fornitori-ordini-list>` (it's a child route, the
  list renders the `<router-outlet>` as a sibling). The legacy used
  a `.fod-modal-overlay` to achieve the same effect; we restored it
  in the restyle after the initial pass shipped a flat full-page
  layout that ended up rendering BELOW the list (off-screen).
  Click on the backdrop closes the modal (= router-navigate back).
-->
<div class="fod-page-backdrop" (click)="goBack()">
  <div class="fod-page" (click)="$event.stopPropagation()">
    @if (loading && !order) {
      <div class="fod-loading-card">
        <div class="fod-spinner"></div>
        <p class="fod-loading-card__msg">Caricamento dettagli ordine…</p>
      </div>
    }

    @if (order) {
      <!-- ─── Sticky header ──────────────────────────────────────── -->
      <header class="fod-header">
        <div class="fod-header__titles">
          <h1 class="fod-header__title">
            📋 Dettagli ordine
            <span
              class="status-pill"
              [attr.data-state]="isStorico ? 'closed' : 'open'"
            >
              @if (isStorico) {
                ● Chiuso
              } @else {
                ● In lavorazione
              }
            </span>
          </h1>
          <p class="fod-header__subtitle">
            {{ order.or_fornitore || "—" }}
            @if (order.or_numero_ordine) {
              · n° {{ order.or_numero_ordine }}
            }
          </p>
        </div>
        <button
          type="button"
          class="modal-close"
          (click)="goBack()"
          aria-label="Chiudi"
        >
          ×
        </button>
      </header>

      <!-- ─── Info cards (4) ─────────────────────────────────────── -->
      <section class="fod-info-row" aria-label="Dati ordine">
        <div class="info-card">
          <span class="info-card__label">N° ordine</span>
          <span class="info-card__value info-card__value--accent">{{
            order.or_numero_ordine || "—"
          }}</span>
        </div>
        <div class="info-card">
          <span class="info-card__label">Fornitore</span>
          <span class="info-card__value">{{ order.or_fornitore || "—" }}</span>
        </div>
        <div class="info-card">
          <span class="info-card__label">Data arrivo</span>
          <span class="info-card__value">{{
            order.or_data_arrivo
              ? (order.or_data_arrivo | date: "dd/MM/yy")
              : "—"
          }}</span>
        </div>
        <div class="info-card">
          <span class="info-card__label">Data presunta evasione</span>
          <span class="info-card__value">{{
            order.or_data_consegna
              ? (order.or_data_consegna | date: "dd/MM/yy")
              : "—"
          }}</span>
        </div>
      </section>

      <!-- ─── DDT card ───────────────────────────────────────────── -->
      <section
        class="fod-ddt-card"
        [class.fod-ddt-card--missing]="!order.ddt_url"
        aria-label="DDT fornitore"
      >
        <div class="fod-ddt-card__icon" aria-hidden="true">
          @if (order.ddt_url) {
            📄
          } @else {
            📭
          }
        </div>
        <div class="fod-ddt-card__text">
          @if (order.ddt_url) {
            <span class="fod-ddt-card__label">DDT fornitore</span>
            <a
              class="fod-ddt-card__link"
              [href]="order.ddt_url"
              target="_blank"
              rel="noopener"
            >
              Apri PDF — caricato il {{ order.ddt_date | date: "dd/MM/yy" }}
            </a>
          } @else {
            <span class="fod-ddt-card__label">Nessun DDT caricato</span>
            <span class="fod-ddt-card__hint">
              @if (!isStorico) {
                Carica il DDT del fornitore per confermare l'ordine.
              } @else {
                Ordine chiuso senza DDT registrato.
              }
            </span>
          }
        </div>
        @if (savingDdt) {
          <div class="fod-spinner-sm"></div>
        }
        @if (!isStorico && order.ddt_url) {
          <button
            type="button"
            class="fod-ddt-card__delete"
            (click)="deleteDdt()"
            title="Elimina DDT"
          >
            🗑️
          </button>
        }
      </section>

      <!-- ─── Products card ──────────────────────────────────────── -->
      <section class="fod-products-card" aria-label="Articoli dell'ordine">
        <header class="fod-products-card__header">
          <h2 class="fod-products-card__title">📦 Articoli</h2>
          <span class="fod-products-card__count">
            {{ products.length }}
            {{ products.length === 1 ? "articolo" : "articoli" }}
            @if (products.length && allPrepared) {
              <span class="fod-all-prepared">· tutti pronti ✓</span>
            }
          </span>
        </header>

        @if (products.length === 0) {
          <p class="fod-empty">Nessun articolo associato a questo ordine.</p>
        } @else {
          <div class="fod-products-scroll">
            <table class="fod-products-table" role="table">
              <thead>
                <tr>
                  <th class="col-product-code">Codice</th>
                  <th class="col-product-desc">Nome articolo</th>
                  <th class="col-product-qty">Quantità</th>
                  <th class="col-product-status">Stato</th>
                  <th class="col-product-actions" aria-label="Azioni"></th>
                </tr>
              </thead>
              <tbody>
                @for (p of products; track p.supplier_certificate_product_id) {
                  <tr
                    class="fod-products-row"
                    [class.fod-products-row--prepared]="p.is_prepared"
                    [class.fod-products-row--selected]="
                      selectedProduct?.supplier_certificate_product_id ===
                      p.supplier_certificate_product_id
                    "
                    (click)="
                      p.fornitore_certificato_prodotti_id
                        ? selectProduct(p)
                        : openProductDetail(p.product_code)
                    "
                    [title]="
                      p.fornitore_certificato_prodotti_id
                        ? 'Apri lotti articolo'
                        : 'Dettagli prodotto'
                    "
                  >
                    <td class="col-product-code">
                      <span class="cell-product-code">{{
                        p.product_code || "—"
                      }}</span>
                    </td>
                    <td class="col-product-desc">
                      <span class="cell-product-desc">{{
                        p.product_description || "—"
                      }}</span>
                    </td>
                    <td class="col-product-qty">
                      <span
                        class="cell-product-qty"
                        [class.cell-product-qty--prepared]="p.is_prepared"
                      >
                        {{ p.quantity ?? 0 }}
                      </span>
                    </td>
                    <td class="col-product-status">
                      @if (p.is_prepared) {
                        <span class="status-pill" data-state="prepared"
                          >● Pronto</span
                        >
                      } @else if (p.fornitore_certificato_prodotti_id) {
                        <span class="status-pill" data-state="pending"
                          >● Da preparare</span
                        >
                      } @else {
                        <span class="status-pill" data-state="unavailable"
                          >● —</span
                        >
                      }
                    </td>
                    <td
                      class="col-product-actions"
                      (click)="$event.stopPropagation()"
                    >
                      @if (p.fornitore_certificato_prodotti_id) {
                        <button
                          type="button"
                          class="icon-btn"
                          title="Apri lotti articolo"
                          (click)="selectProduct(p)"
                        >
                          📋
                        </button>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </section>

      <!-- ─── Bottom actions ─────────────────────────────────────── -->
      @if (!isStorico) {
        <footer class="fod-bottom-actions">
          <button
            type="button"
            class="btn btn--secondary"
            (click)="openDdtUpload()"
            [disabled]="saving || savingDdt"
          >
            @if (savingDdt) {
              <span class="btn-spinner"></span>
            }
            📄 {{ hasDdt ? "Ricarica DDT fornitore" : "Carica DDT fornitore" }}
          </button>
          @if (allPrepared && hasDdt) {
            <button
              type="button"
              class="btn btn--success"
              (click)="confirmOrder()"
              [disabled]="saving"
            >
              @if (saving) {
                <span class="btn-spinner"></span>
              }
              ✓ Conferma ordine
            </button>
          }
        </footer>
      }
    }
  </div>
</div>

<!-- ──────────────────────────────────────────────────────────────────
     MODAL 1 — Product lots list
     Rendered OUTSIDE `.fod-page` to insulate from containing-block
     issues (Ionic `.ion-page` + `.fod-page` scrolling container).
     ─────────────────────────────────────────────────────────────── -->
@if (selectedProduct) {
  <div class="modal-backdrop" (click)="closePanel()">
    <aside class="modal modal--md" (click)="$event.stopPropagation()">
      <header class="modal__header">
        <h2 class="modal__title">
          📦 Articolo <strong>{{ selectedProduct.product_code }}</strong>
        </h2>
        <button
          type="button"
          class="modal-close"
          (click)="closePanel()"
          aria-label="Chiudi"
        >
          ×
        </button>
      </header>
      <div class="modal__body">
        <p class="modal__hint">
          {{ selectedProduct.product_description || "—" }}
          @if (selectedProduct.batches.length) {
            · {{ selectedProduct.batches.length }}
            {{ selectedProduct.batches.length === 1 ? "lotto" : "lotti" }}
          }
        </p>

        @if (selectedProduct.batches.length === 0) {
          <div class="modal-empty">
            <div class="modal-empty__icon" aria-hidden="true">📭</div>
            <p>Nessun lotto presente.</p>
          </div>
        } @else {
          <ul class="lots-list" role="list">
            @for (
              batch of selectedProduct.batches;
              track batch.supplier_certificate_batch_id
            ) {
              <li class="lot-card" (click)="viewLot(batch)">
                <div class="lot-card__main">
                  <span class="lot-card__code">
                    {{ selectedProduct.product_code }}${{
                      batch.batch_week | number: "2.0-0"
                    }}{{ `${batch.batch_year}` | slice: -2 }}${{
                      batch.serial_from | number: "2.0-0"
                    }}-{{ batch.serial_to | number: "2.0-0" }}
                  </span>
                  <span class="lot-card__meta">
                    Sett. {{ batch.batch_week }}/{{ batch.batch_year }} ·
                    seriali {{ batch.serial_from }}–{{ batch.serial_to }}
                    @if (batch.documents?.length) {
                      · {{ batch.documents.length }} cert.
                    }
                  </span>
                </div>
                <span class="lot-card__qty">{{ batch.quantity }} pz</span>
                <span class="lot-card__chevron" aria-hidden="true">›</span>
              </li>
            }
          </ul>
        }
      </div>
      @if (!isStorico) {
        <footer class="modal__footer">
          <button type="button" class="btn btn--ghost" (click)="closePanel()">
            Chiudi
          </button>
          <button
            type="button"
            class="btn btn--primary"
            (click)="startAddLot()"
          >
            + Aggiungi lotto
          </button>
        </footer>
      }
    </aside>
  </div>
}

<!-- ──────────────────────────────────────────────────────────────────
     MODAL 2 — Batch wizard (view-lot / add-step1 / add-step2 / add-step3)
     ─────────────────────────────────────────────────────────────── -->
@if (selectedProduct && panelView !== "lots") {
  <div class="modal-backdrop modal-backdrop--nested" (click)="backToLots()">
    <aside class="modal modal--lg" (click)="$event.stopPropagation()">
      <!-- ─── View lot (read-only) ─────────────────────────────── -->
      @if (panelView === "view-lot" && selectedBatch) {
        <header class="modal__header">
          <h2 class="modal__title">
            🔍 Lotto
            <strong>
              {{ selectedProduct.product_code }}${{
                selectedBatch.batch_week | number: "2.0-0"
              }}{{ `${selectedBatch.batch_year}` | slice: -2 }}${{
                selectedBatch.serial_from | number: "2.0-0"
              }}-{{ selectedBatch.serial_to | number: "2.0-0" }}
            </strong>
          </h2>
          <button
            type="button"
            class="modal-close"
            (click)="backToLots()"
            aria-label="Chiudi"
          >
            ×
          </button>
        </header>
        <div class="modal__body">
          <div class="range-step">
            <section class="range-card">
              <h3 class="range-card__title">📅 Periodo</h3>
              <div class="range-card__values">
                <div class="range-value">
                  <span class="range-value__label">Settimana</span>
                  <span class="range-value__display">{{
                    selectedBatch.batch_week
                  }}</span>
                </div>
                <div class="range-value">
                  <span class="range-value__label">Anno</span>
                  <span class="range-value__display">{{
                    selectedBatch.batch_year
                  }}</span>
                </div>
              </div>
            </section>
            <section class="range-card">
              <h3 class="range-card__title">🔢 Range seriali</h3>
              <div class="range-card__values range-card__values--3">
                <div class="range-value">
                  <span class="range-value__label">Quantità</span>
                  <span
                    class="range-value__display range-value__display--accent"
                    >{{ selectedBatch.qta }}</span
                  >
                </div>
                <div class="range-value">
                  <span class="range-value__label">Da</span>
                  <span class="range-value__display">{{
                    selectedBatch.serial_from
                  }}</span>
                </div>
                <div class="range-value">
                  <span class="range-value__label">A</span>
                  <span class="range-value__display">{{
                    selectedBatch.serial_to
                  }}</span>
                </div>
              </div>
            </section>
          </div>

          <section class="certs-section">
            <button
              type="button"
              class="certs-section__header"
              (click)="toggleCerts()"
              [attr.aria-expanded]="certsExpanded"
            >
              <span>📎 Certificati e DDT</span>
              <span
                class="certs-section__chevron"
                [class.certs-section__chevron--open]="certsExpanded"
                aria-hidden="true"
                >▾</span
              >
            </button>
            @if (certsExpanded) {
              <div class="certs-section__body">
                @if (selectedBatch.documents.length === 0) {
                  <p class="certs-empty">
                    Nessun certificato caricato per questo lotto.
                  </p>
                } @else {
                  @for (
                    doc of selectedBatch.documents;
                    track doc.supplier_certificate_document_id
                  ) {
                    <div class="cert-row">
                      <span class="cert-row__icon" aria-hidden="true">📄</span>
                      <span class="cert-row__name">{{
                        doc.document_description || "Certificato"
                      }}</span>
                      @if (doc.document_check === "S" || doc.document_check === "N") {
                        <button
                          type="button"
                          class="cert-row__check"
                          [class.cert-row__check--ok]="doc.document_check === 'S'"
                          [class.cert-row__check--ko]="doc.document_check === 'N'"
                          (click)="openCertInfo(doc)"
                          title="Vedi dettagli validazione"
                        >
                          {{ doc.document_check === "S" ? "✓" : "✕" }}
                        </button>
                      } @else if (!isStorico) {
                        <button
                          type="button"
                          class="cert-row__validate"
                          (click)="openValidateConfirm(doc)"
                          title="Valida certificato"
                        >
                          ✓
                        </button>
                      }
                      @if (doc.document_url) {
                        <a
                          class="cert-row__open"
                          [href]="doc.document_url"
                          target="_blank"
                          rel="noopener"
                          title="Apri certificato"
                          >📄 Apri</a
                        >
                      }
                      @if (!isStorico) {
                        <button
                          type="button"
                          class="cert-row__delete"
                          (click)="
                            deleteCertFile(doc.supplier_certificate_document_id)
                          "
                          title="Elimina certificato"
                        >
                          🗑️
                        </button>
                      }
                    </div>
                  }
                }
              </div>
            }
          </section>
        </div>
        <footer class="modal__footer modal__footer--split">
          <button type="button" class="btn btn--ghost" (click)="backToLots()">
            ‹ Torna ai lotti
          </button>
          @if (!isStorico) {
            <div class="modal__footer-right">
              <button
                type="button"
                class="btn btn--secondary"
                (click)="editLot(selectedBatch)"
              >
                ✏️ Modifica
              </button>
              <button
                type="button"
                class="btn btn--primary"
                (click)="startAddCertForBatch(selectedBatch)"
              >
                + Aggiungi cert.
              </button>
            </div>
          }
        </footer>
      }

      <!-- ─── Step 1: form lotto (new or edit) ─────────────────── -->
      @if (panelView === "add-step1") {
        <header class="modal__header">
          <h2 class="modal__title">
            {{ pendingBatchId ? "✏️ Modifica lotto" : "➕ Nuovo lotto" }}
          </h2>
          <button
            type="button"
            class="modal-close"
            (click)="backToLots()"
            aria-label="Chiudi"
          >
            ×
          </button>
        </header>
        <div class="modal__body">
          <div class="range-step">
            <section class="range-card">
              <h3 class="range-card__title">📅 Scegli periodo</h3>
              <div class="range-card__values">
                <label class="range-value">
                  <span class="range-value__label">Settimana</span>
                  <input
                    type="number"
                    class="range-value__input"
                    [(ngModel)]="newBatch.batch_week"
                    min="1"
                    max="53"
                    (focus)="$any($event.target).select()"
                  />
                </label>
                <label class="range-value">
                  <span class="range-value__label">Anno</span>
                  <input
                    type="number"
                    class="range-value__input"
                    [(ngModel)]="newBatch.batch_year"
                    min="2000"
                    (focus)="$any($event.target).select()"
                  />
                </label>
              </div>
            </section>
            <section class="range-card">
              <h3 class="range-card__title">🔢 Scegli range</h3>
              <div class="range-card__values range-card__values--3">
                <label class="range-value">
                  <span class="range-value__label">Quantità</span>
                  <input
                    type="number"
                    class="range-value__input"
                    [(ngModel)]="newBatch.qta"
                    min="1"
                    (focus)="$any($event.target).select()"
                  />
                </label>
                <label class="range-value">
                  <span class="range-value__label">Da</span>
                  <input
                    type="number"
                    class="range-value__input"
                    [(ngModel)]="newBatch.serial_from"
                    min="0"
                    (focus)="$any($event.target).select()"
                  />
                </label>
                <label class="range-value">
                  <span class="range-value__label">A</span>
                  <input
                    type="number"
                    class="range-value__input"
                    [(ngModel)]="newBatch.serial_to"
                    min="0"
                    (focus)="$any($event.target).select()"
                  />
                </label>
              </div>
            </section>
          </div>
          @if (saving) {
            <div class="saving-row">
              <span class="btn-spinner btn-spinner--dark"></span>
              <span>Salvataggio lotto…</span>
            </div>
          }
        </div>
        <footer class="modal__footer modal__footer--split">
          <button
            type="button"
            class="btn btn--ghost"
            (click)="backToLots()"
            [disabled]="saving"
          >
            ‹ Annulla
          </button>
          <button
            type="button"
            class="btn btn--primary"
            (click)="submitStep1()"
            [disabled]="saving"
          >
            @if (saving) {
              <span class="btn-spinner"></span>
            }
            Continua →
          </button>
        </footer>
      }

      <!-- ─── Step 2: upload certificato ───────────────────────── -->
      @if (panelView === "add-step2") {
        <header class="modal__header">
          <h2 class="modal__title">📎 Carica certificato</h2>
          <button
            type="button"
            class="modal-close"
            (click)="backToLots()"
            aria-label="Chiudi"
          >
            ×
          </button>
        </header>
        <div class="modal__body">
          <p class="modal__hint">
            Carica un certificato PDF per questo lotto. Puoi anche saltare e
            aggiungerlo successivamente.
          </p>
          <button
            type="button"
            class="upload-zone"
            (click)="triggerCertUpload()"
            [disabled]="saving"
          >
            <span class="upload-zone__icon" aria-hidden="true">☁️</span>
            <span class="upload-zone__msg"
              >Clicca per selezionare un file PDF</span
            >
            <span class="upload-zone__hint"
              >Formato accettato: <strong>.pdf</strong></span
            >
          </button>
          @if (saving) {
            <div class="saving-row">
              <span class="btn-spinner btn-spinner--dark"></span>
              <span>Caricamento certificato…</span>
            </div>
          }
        </div>
        <footer class="modal__footer modal__footer--split">
          <button
            type="button"
            class="btn btn--ghost"
            (click)="backToLots()"
            [disabled]="saving"
          >
            ‹ Annulla
          </button>
          <button
            type="button"
            class="btn btn--secondary"
            (click)="saveLotDone()"
            [disabled]="saving"
          >
            Salva senza certificato
          </button>
        </footer>
      }

      <!-- ─── Step 3: lista certificati caricati ───────────────── -->
      @if (panelView === "add-step3") {
        <header class="modal__header">
          <h2 class="modal__title">📎 Certificati caricati</h2>
          <button
            type="button"
            class="modal-close"
            (click)="backToLots()"
            aria-label="Chiudi"
          >
            ×
          </button>
        </header>
        <div class="modal__body">
          <p class="modal__hint">
            Puoi aggiungere altri certificati allo stesso lotto o salvare e
            chiudere.
          </p>
          <div class="certs-section__body">
            @if (getBatchDocs().length === 0) {
              <p class="certs-empty">Nessun certificato caricato.</p>
            } @else {
              @for (
                doc of getBatchDocs();
                track doc.supplier_certificate_document_id
              ) {
                <div class="cert-row">
                  <span class="cert-row__icon" aria-hidden="true">📄</span>
                  <span class="cert-row__name">{{
                    doc.document_description || "Certificato"
                  }}</span>
                  @if (doc.document_url) {
                    <a
                      class="cert-row__open"
                      [href]="doc.document_url"
                      target="_blank"
                      rel="noopener"
                      >📄 Apri</a
                    >
                  }
                  <button
                    type="button"
                    class="cert-row__delete"
                    (click)="
                      deleteCertFile(doc.supplier_certificate_document_id)
                    "
                    title="Elimina certificato"
                  >
                    🗑️
                  </button>
                </div>
              }
            }
          </div>
        </div>
        <footer class="modal__footer modal__footer--split">
          <button
            type="button"
            class="btn btn--secondary"
            (click)="addAnotherCert()"
          >
            + Aggiungi altro
          </button>
          <button
            type="button"
            class="btn btn--primary"
            (click)="saveLotDone()"
          >
            ✓ Salva e chiudi
          </button>
        </footer>
      }
    </aside>
  </div>
}

<!-- ──────────────────────────────────────────────────────────────────
     MODAL 3 — Valida certificato (confirm) / MODAL 4 — Info validazione
     Stack sopra Modal 2 (1300/1400) — z-index 1500/1600.
     ─────────────────────────────────────────────────────────────── -->
@if (certToValidate) {
  <div class="modal-backdrop modal-backdrop--cert" (click)="cancelValidate()">
    <aside class="modal-cert" (click)="$event.stopPropagation()">
      <h2 class="modal-cert__title">Valida certificati</h2>
      <p class="modal-cert__text">Confermi che il certificato è valido?</p>
      <div class="modal-cert__actions">
        <button
          type="button"
          class="modal-cert__btn modal-cert__btn--yes"
          [disabled]="validatingCert"
          (click)="confirmValidate()"
        >
          <span aria-hidden="true">✓</span> SI
        </button>
        <button
          type="button"
          class="modal-cert__btn modal-cert__btn--no"
          [disabled]="validatingCert"
          (click)="cancelValidate()"
        >
          <span aria-hidden="true">🚫</span> NO
        </button>
      </div>
    </aside>
  </div>
}

@if (certToView) {
  <div class="modal-backdrop modal-backdrop--cert" (click)="closeCertInfo()">
    <aside class="modal-cert" (click)="$event.stopPropagation()">
      <h2 class="modal-cert__title">VALIDATO</h2>
      <p class="modal-cert__text">
        Il certificato è stato validato da
        <strong>{{ certToView.document_check_operator || "—" }}</strong>
        {{ formatCertCheckDate(certToView.document_check_date) }}
      </p>
    </aside>
  </div>
}

<!-- Hidden file inputs (triggered programmatically) -->
<input
  #certFileInput
  type="file"
  accept=".pdf"
  hidden
  (change)="onCertSelected($event)"
/>
<input
  #ddtFileInput
  type="file"
  accept=".pdf"
  hidden
  (change)="onDdtFileSelected($event)"
/>

<!--
  🚨 v2.12.1 R.2: product detail modal triggered da click sulle righe
  prodotti SENZA certificato (per quelle con certificato il click apre
  i lotti via selectProduct). Pattern Diakonix B.3 v2.7.0.
-->
@if (closeSrv.isProdDetOn) {
  <app-product-detail-modal
    [opened]="closeSrv.isProdDetOn"
    [pr_code]="openedProductCode || ''"
  ></app-product-detail-modal>
}
